/**
 * TenantSwitcher — the ⌘K palette for jumping between tenants anywhere in
 * the super-admin console.
 *
 * It is a TREE, not a list. The first version showed a flat, capped set of
 * rows (recents, starred, then "some of the rest"), which hid most tenants
 * and every bit of hierarchy — the operator could not tell which company a
 * team belonged to, or even that a tenant existed. Now every tenant is
 * shown in its place under its parent, with connector guides, level icons
 * and user counts; the search keeps each match's ancestors (dimmed) so a
 * result is never shown out of context.
 *
 * One component, mounted once per page that carries tenant context. Opens
 * on ⌘K / Ctrl+K (or the "Switch tenant" button) and calls `onSelect` — the
 * caller decides where to navigate (the contract: switching PRESERVES
 * place — same section, same brand group).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { getInitials } from '@/utils/getInitials';
import { getRecentTenants, subscribeConsoleState } from './consoleState';
import TenantTreeView, { useTenantTreeRows } from './TenantTreeView';
import './console.css';
import './console-shell.css';

export interface SwitcherTenant {
  tenant_id: string;
  tenant_name: string;
  tenant_code: string;
  parent_tenant_id?: string | null;
  user_count?: number;
  /** The tenant's own brand logo, when it has overridden one. Callers read it
   *  off tenant_settings.brand_overrides; the chip prefers it over initials. */
  logo?: string | null;
}

/**
 * TenantChip — the tenant's avatar.
 *
 * A tenant that has uploaded a logo IS that logo to the people who run it, so
 * the chip shows it. Initials on a hashed gradient are the fallback for the
 * many tenants that never set one — deterministic, so a given tenant always
 * looks the same. Rendered here rather than inline in three places so the
 * context bar, the switcher tree and the recents chips cannot drift apart.
 */
export function TenantChip({
  tenant,
  size = 'sm',
}: {
  tenant: Pick<SwitcherTenant, 'tenant_id' | 'tenant_name' | 'logo'>;
  size?: 'sm' | 'lg';
}) {
  const cls = `sa-tenant-chip${size === 'lg' ? ' sa-tenant-chip--lg' : ''}`;
  if (tenant.logo) {
    return (
      <span className={`${cls} sa-tenant-chip--logo`} aria-hidden>
        <img src={tenant.logo} alt="" />
      </span>
    );
  }
  const hue = tenantChipHue(tenant.tenant_id);
  return (
    <span
      className={cls}
      style={{ background: `linear-gradient(135deg, hsl(${hue} 65% 45%), hsl(${(hue + 40) % 360} 65% 35%))` }}
      aria-hidden
    >
      {getInitials(tenant.tenant_name)}
    </span>
  );
}

interface Props {
  tenants: SwitcherTenant[];
  /** The tenant currently in context — marked "current" and not selectable,
   *  so the palette never offers a no-op switch. */
  currentTenantId?: string | null;
  open: boolean;
  onClose: () => void;
  onSelect: (tenantId: string) => void;
}

/** Platform-aware label for the switcher shortcut — ⌘K on Mac, Ctrl+K
 *  elsewhere. Rendered in the context bar and the directory toolbar. */
export const SWITCHER_KEY_LABEL: string =
  typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.platform)
    ? '⌘K'
    : 'Ctrl+K';

/** Deterministic accent for a tenant chip — same recipe everywhere. */
export function tenantChipHue(tenantId: string): number {
  let h = 0;
  for (let i = 0; i < tenantId.length; i++) h = (h * 31 + tenantId.charCodeAt(i)) % 360;
  return h;
}

/* ─── Component ───────────────────────────────────────────────────────── */

export default function TenantSwitcher({ tenants, currentTenantId, open, onClose, onSelect }: Props) {
  const { t } = useLanguage();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  // Bump on any consoleState change so recents refresh live.
  const [, setStateTick] = useState(0);
  useEffect(() => subscribeConsoleState(() => setStateTick(n => n + 1)), []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      // Focus after the overlay paints.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const byId = useMemo(() => {
    const m = new Map<string, SwitcherTenant>();
    tenants.forEach(tn => m.set(tn.tenant_id, tn));
    return m;
  }, [tenants]);

  const q = query.trim().toLowerCase();
  // Every tenant, always — the switcher's whole job is showing the shape of
  // the platform, so it never collapses branches.
  const rows = useTenantTreeRows(tenants, query);

  /** Indices of rows the keyboard may land on (everything but "current"). */
  const selectable = useMemo(
    () => rows.map((r, i) => (r.tenant.tenant_id === currentTenantId ? -1 : i)).filter(i => i >= 0),
    [rows, currentTenantId],
  );

  const matchCount = useMemo(() => (q ? rows.filter(r => r.match).length : rows.length), [rows, q]);

  // Keep the highlighted row valid as the list changes, and in view.
  useEffect(() => {
    setActiveIndex(i => Math.min(i, Math.max(0, selectable.length - 1)));
  }, [selectable.length]);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, q]);

  const recents = useMemo(
    () => getRecentTenants()
      .map(r => byId.get(r.tenantId))
      .filter((tn): tn is SwitcherTenant => !!tn && tn.tenant_id !== currentTenantId)
      .slice(0, 6),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [byId, currentTenantId, open],
  );

  const pick = useCallback((tenantId: string) => {
    if (tenantId === currentTenantId) return;
    onClose();
    onSelect(tenantId);
  }, [onClose, onSelect, currentTenantId]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, selectable.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const rowIndex = selectable[activeIndex];
      const row = rowIndex !== undefined ? rows[rowIndex] : undefined;
      if (row) pick(row.tenant.tenant_id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  if (!open) return null;

  const activeRowIndex = selectable[activeIndex];

  return (
    <div
      className="sa-switcher-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t('superAdmin.console.switchTenant')}
      onClick={onClose}
      data-testid="tenant-switcher"
    >
      <div className="sa-switcher sa-switcher--tree" onClick={e => e.stopPropagation()}>
        <div className="sa-switcher-head">
          <input
            ref={inputRef}
            className="sa-switcher-input"
            type="text"
            placeholder={t('superAdmin.console.switcherPlaceholder')}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            data-testid="tenant-switcher-input"
          />
          <span className="sa-switcher-total" data-testid="tenant-switcher-count">
            {q
              ? t('superAdmin.directory.matches').replace('{{count}}', String(matchCount))
              : t('superAdmin.console.switcherTotal').replace('{{count}}', String(tenants.length))}
          </span>
        </div>

        {!q && recents.length > 0 && (
          <div className="sa-switcher-recents" data-testid="tenant-switcher-recents">
            <span className="sa-switcher-recents-label">{t('superAdmin.console.switcherRecent')}</span>
            {recents.map(tn => {
              return (
                <button
                  key={tn.tenant_id}
                  type="button"
                  className="sa-switcher-recent"
                  onClick={() => pick(tn.tenant_id)}
                >
                  <TenantChip tenant={tn} />
                  {tn.tenant_name}
                </button>
              );
            })}
          </div>
        )}

        <div className="sa-switcher-tree" role="tree" data-testid="tenant-switcher-tree">
          {rows.length === 0 && (
            <div className="sa-switcher-empty">{t('superAdmin.console.switcherNoMatch')}</div>
          )}
          <TenantTreeView
            rows={rows}
            query={query}
            currentTenantId={currentTenantId}
            activeTenantId={activeRowIndex !== undefined ? rows[activeRowIndex]?.tenant.tenant_id : null}
            onSelect={pick}
            onHover={(id) => {
              const idx = rows.findIndex(r => r.tenant.tenant_id === id);
              const k = selectable.indexOf(idx);
              if (k >= 0) setActiveIndex(k);
            }}
            activeRef={activeRef}
          />
        </div>

        <div className="sa-switcher-footer">
          <span><kbd>↑↓</kbd> {t('superAdmin.console.switcherNav')}</span>
          <span><kbd>↵</kbd> {t('superAdmin.console.switcherPick')}</span>
          <span><kbd>esc</kbd> {t('common.close')}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook: binds ⌘K / Ctrl+K to open the switcher. Returns [open, setOpen].
 * Mount once per page that carries tenant context. Pass `enabled: false`
 * when another switcher owner is active on the same page — otherwise one
 * keystroke opens two overlays.
 */
export function useTenantSwitcherHotkey(enabled = true): [boolean, (v: boolean) => void] {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!enabled) {
      setOpen(false);
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
  return [open, setOpen];
}
